/**
 * FAQ Modal Component
 * 
 * SESSION 122: Trading FAQ for users
 * Displays frequently asked questions about trading signals and system usage
 */

import React, { useState } from 'react';
import { X, HelpCircle, ChevronDown, ChevronUp } from 'lucide-react';

const FAQModal = ({ isOpen, onClose }) => {
  const [expandedIndex, setExpandedIndex] = useState(null);

  const faqs = [
    {
      question: "Is there a way to tell the user that score above 100 is required to enter into trade?",
      answer: "A score of 100 doesn't always mean a buy, because there are some strict vetoes also. If the trade passes all the vetoes and the score is above a given level, then the trade is fired in the Signal section."
    },
    {
      question: "If the score is above 100, is there a way to know if I can still enter the trade? (e.g., signal came at 10am and I look at 11am with score still 100+)",
      answer: "There is always a T1 and SL mentioned in the trade. Otherwise one can follow strict target of 50 points and SL of 30 points. If the user looks at it late, then they should ideally wait for another signal."
    },
    {
      question: "Is there any description for Equity signals that these are intraday only and users can trade in futures?",
      answer: "Most of the trades are advised intraday but one can carry it for maximum 1-2 days. Trade prices are in futures only."
    },
    {
      question: "How is the trade shown once it is complete?",
      answer: "It will show closed as either T1 or SL hit."
    },
    {
      question: "Are there any other info that the user should look at apart from the signal-log?",
      answer: "For scalpers, if they see a score above 90 but do not see it in signal section, they can trade with strict profit of 20 points and SL of 20 points too."
    }
  ];

  const toggleExpand = (index) => {
    setExpandedIndex(expandedIndex === index ? null : index);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="relative w-full max-w-2xl max-h-[85vh] mx-4 bg-[#0d1117] border border-cyan-500/30 rounded-xl shadow-2xl shadow-cyan-500/10 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-cyan-500/20 bg-gradient-to-r from-cyan-500/10 to-transparent">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-cyan-500/20">
              <HelpCircle className="w-5 h-5 text-cyan-400" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-white">Frequently Asked Questions</h2>
              <p className="text-xs text-gray-400">Trading signals & system usage guide</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-white/10 transition-colors"
            data-testid="faq-close-btn"
          >
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        {/* FAQ Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(85vh-80px)] custom-scrollbar">
          <div className="space-y-3">
            {faqs.map((faq, index) => (
              <div
                key={index}
                className={`border rounded-lg overflow-hidden transition-all duration-200 ${
                  expandedIndex === index 
                    ? 'border-cyan-500/50 bg-cyan-500/5' 
                    : 'border-gray-700/50 hover:border-gray-600/50'
                }`}
              >
                {/* Question */}
                <button
                  onClick={() => toggleExpand(index)}
                  className="w-full flex items-start justify-between gap-4 p-4 text-left"
                  data-testid={`faq-question-${index}`}
                >
                  <div className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-cyan-500/20 text-cyan-400 text-xs font-bold flex items-center justify-center mt-0.5">
                      {index + 1}
                    </span>
                    <span className="text-sm font-medium text-gray-200 leading-relaxed">
                      {faq.question}
                    </span>
                  </div>
                  <div className="flex-shrink-0 mt-0.5">
                    {expandedIndex === index ? (
                      <ChevronUp className="w-4 h-4 text-cyan-400" />
                    ) : (
                      <ChevronDown className="w-4 h-4 text-gray-500" />
                    )}
                  </div>
                </button>

                {/* Answer - Collapsible */}
                <div
                  className={`overflow-hidden transition-all duration-300 ease-in-out ${
                    expandedIndex === index ? 'max-h-96' : 'max-h-0'
                  }`}
                >
                  <div className="px-4 pb-4 pt-0">
                    <div className="ml-9 pl-4 border-l-2 border-cyan-500/30">
                      <p className="text-sm text-cyan-300 font-semibold italic leading-relaxed">
                        {faq.answer}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Footer Note */}
          <div className="mt-6 p-4 rounded-lg bg-amber-500/10 border border-amber-500/20">
            <p className="text-xs text-amber-400/80 text-center">
              <span className="font-semibold">Pro Tip:</span> Always check the Signal section for confirmed trades with defined T1/SL levels before entering any position.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FAQModal;
